import React, { useEffect, useRef, memo, useState } from 'react';
import styles from './TradingViewChart.module.css';
import { useTheme } from '../../../contexts/ThemeContext';
import logger from '../../../logging/logger';

interface EmbeddedTradingViewChartProps {
  symbol: string;
  comparison_symbols?: string[];
  timeframe?: string;
  theme?: 'light' | 'dark';
  chart_type?: 'single' | 'comparison' | 'overlay';
  indicators?: string[];
}

export const EmbeddedTradingViewChart: React.FC<EmbeddedTradingViewChartProps> = memo(({
  symbol,
  comparison_symbols = [],
  timeframe = 'D',
  theme: themeProp,
  chart_type = 'single',
  indicators = []
}) => {
  const { theme: appTheme } = useTheme();
  const theme = themeProp ?? appTheme;
  const containerRef = useRef<HTMLDivElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!containerRef.current || !symbol) {
      logger.info('EmbeddedTradingViewChart: Missing container or symbol', { containerRef: containerRef.current, symbol });
      return;
    }

    logger.info('EmbeddedTradingViewChart: Creating widget for', { symbol, comparison_symbols, timeframe, theme, chart_type, indicators });

    // Set loading state
    setIsLoading(true);

    // Clear any existing content
    containerRef.current.innerHTML = '';

    // Create the widget container
    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container';
    widgetContainer.style.height = '100%';
    widgetContainer.style.width = '100%';

    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'tradingview-widget-container__widget';
    widgetDiv.style.height = 'calc(100% - 32px)';
    widgetDiv.style.width = '100%';

    const copyrightDiv = document.createElement('div');
    copyrightDiv.className = 'tradingview-widget-copyright';
    copyrightDiv.innerHTML = `
      <a href="https://www.tradingview.com/symbols/${symbol}/" rel="noopener nofollow" target="_blank">
        <span class="blue-text">${symbol} stock chart</span>
      </a>
      <span class="trademark"> by TradingView</span>
    `;

    widgetContainer.appendChild(widgetDiv);
    widgetContainer.appendChild(copyrightDiv);

    // Create and configure the script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;

    // Widget configuration
    const formattedSymbol = formatSymbol(symbol);
    const formattedComparisonSymbols = comparison_symbols.map(formatSymbol);
    
    // Determine chart style based on type
    let chartStyle = '1'; // Default candlestick
    if (chart_type === 'comparison' || chart_type === 'overlay') {
      chartStyle = '3'; // Line chart for comparison and overlay
    }
    
    // For overlay and comparison charts, format comparison symbols properly
    // TradingView embedded widget expects compareSymbols as array of objects with symbol property
    const compareSymbolsConfig = formattedComparisonSymbols.map(sym => ({
      symbol: sym,
      position: "SameScale" // This ensures overlay behavior
    }));
    
    // Map indicators to TradingView studies
    const studies = mapIndicatorsToStudies(indicators);
    
    const config = {
      autosize: true,
      symbol: formattedSymbol,
      interval: timeframe,
      timezone: 'Etc/UTC',
      theme: theme,
      style: chartStyle,
      locale: 'en',
      backgroundColor: '#131722',
      gridColor: 'rgba(240, 243, 250, 0.06)',
      allow_symbol_change: true,
      calendar: false,
      details: true, // Show details for better UX
      hide_side_toolbar: false,
      hide_top_toolbar: false,
      hide_legend: false, // Always show legend for better visibility
      hide_volume: chart_type === 'overlay', // Only hide volume for overlay charts
      hotlist: false,
      save_image: true,
      withdateranges: true, // Enable date range selector
      studies: studies,
      watchlist: [],
      // Add comparison symbols if present - use proper format for TradingView embedded widget
      ...(compareSymbolsConfig.length > 0 && {
        compareSymbols: compareSymbolsConfig
      })
    };

    logger.info('EmbeddedTradingViewChart: Widget config', { 
      originalSymbol: symbol, 
      formattedSymbol, 
      originalComparisonSymbols: comparison_symbols,
      formattedComparisonSymbols,
      compareSymbolsConfig,
      chart_type,
      config 
    });

    script.innerHTML = JSON.stringify(config);
    
    // Add error handling for script loading
    script.onerror = () => {
      logger.error('Failed to load TradingView script');
      setIsLoading(false);
      if (containerRef.current) {
        containerRef.current.innerHTML = '<div style="padding: 20px; text-align: center; color: #666;">Failed to load chart. Please try again.</div>';
      }
    };
    
    // Add load handler to hide loading state
    script.onload = () => {
      setTimeout(() => setIsLoading(false), 1000); // Give widget time to render
    };
    
    widgetDiv.appendChild(script);
    containerRef.current.appendChild(widgetContainer);

    // Cleanup function - capture ref to avoid stale closure
    const currentContainerRef = containerRef.current;
    return () => {
      if (currentContainerRef) {
        currentContainerRef.innerHTML = '';
      }
    };
  }, [symbol, comparison_symbols, timeframe, theme, chart_type, indicators]);

  // Map indicator names to TradingView study configurations
  const mapIndicatorsToStudies = (indicatorList: string[]): any[] => {
    const indicatorMap: { [key: string]: any } = {
      'RSI': {
        id: 'RSI@tv-basicstudies',
        inputs: { length: 14 }
      },
      'MACD': {
        id: 'MACD@tv-basicstudies',
        inputs: { fastLength: 12, slowLength: 26, signalLength: 9 }
      },
      'BB': {
        id: 'BB@tv-basicstudies',
        inputs: { length: 20, mult: 2 }
      },
      'MA': {
        id: 'MA@tv-basicstudies',
        inputs: { length: 50 }
      },
      'EMA': {
        id: 'EMA@tv-basicstudies',
        inputs: { length: 21 }
      },
      'Stoch': {
        id: 'Stoch@tv-basicstudies',
        inputs: { k: 14, d: 3, smooth: 3 }
      },
      'ATR': {
        id: 'ATR@tv-basicstudies',
        inputs: { length: 14 }
      },
      'VWAP': {
        id: 'VWAP@tv-basicstudies',
        inputs: {}
      },
      'OBV': {
        id: 'OBV@tv-basicstudies',
        inputs: {}
      },
      'ADX': {
        id: 'ADX@tv-basicstudies',
        inputs: { adxlen: 14, dilen: 14 }
      },
      'PSAR': {
        id: 'PSAR@tv-basicstudies',
        inputs: { start: 0.02, increment: 0.02, maximum: 0.2 }
      },
      'CCI': {
        id: 'CCI@tv-basicstudies',
        inputs: { length: 20 }
      },
      'WilliamsR': {
        id: 'WilliamsR@tv-basicstudies',
        inputs: { length: 14 }
      }
    };

    return indicatorList
      .map(indicator => indicatorMap[indicator])
      .filter(study => study !== undefined);
  };

  // Format symbol for TradingView (add exchange prefix if needed)
  const formatSymbol = (sym: string): string => {
    const upperSym = sym.toUpperCase();
    
    // If already has exchange prefix, return as-is
    if (upperSym.includes(':')) {
      return upperSym;
    }
    
    // ETFs and index funds (NYSE Arca)
    const nyseArcaSymbols = ['SPY', 'QQQ', 'IWM', 'EFA', 'VTI', 'VOO', 'IVV', 'XLF', 'XLE', 'XLK'];
    if (nyseArcaSymbols.includes(upperSym)) {
      return `AMEX:${upperSym}`;  // TradingView uses AMEX for NYSE Arca
    }
    
    // Major tech stocks and popular NASDAQ stocks
    const nasdaqStocks = [
      'AAPL', 'MSFT', 'GOOGL', 'GOOG', 'AMZN', 'TSLA', 'META', 'NVDA', 'NFLX',
      'ADBE', 'CRM', 'INTC', 'AMD', 'PYPL', 'NFLX', 'CMCSA', 'PEP', 'COST',
      'AVGO', 'TXN', 'QCOM', 'INTU', 'AMGN', 'SBUX', 'GILD', 'MDLZ', 'ISRG'
    ];
    if (nasdaqStocks.includes(upperSym)) {
      return `NASDAQ:${upperSym}`;
    }
    
    // NYSE stocks
    const nyseStocks = [
      'JPM', 'JNJ', 'V', 'PG', 'UNH', 'HD', 'MA', 'DIS', 'BAC', 'XOM',
      'WMT', 'CVX', 'LLY', 'ABBV', 'PFE', 'KO', 'TMO', 'ACN', 'NKE', 'MRK',
      'DHR', 'VZ', 'ABT', 'ORCL', 'CRM', 'ADBE', 'WFC', 'BMY', 'PM', 'T'
    ];
    if (nyseStocks.includes(upperSym)) {
      return `NYSE:${upperSym}`;
    }
    
    // Default to NASDAQ for other symbols (most common for US stocks)
    return `NASDAQ:${upperSym}`;
  };

  return (
    <>
      <div className={styles.chartWrapper}>
        <div className={styles.chartHeader}>
          <div className={styles.symbolInfo}>
            <h3 className={styles.symbol}>
              {symbol.toUpperCase()}
              {comparison_symbols.length > 0 && (
                <span>
                  {chart_type === 'overlay' ? ' + ' : ' vs '}
                  {comparison_symbols.map(s => s.toUpperCase()).join(chart_type === 'overlay' ? ' + ' : ', ')}
                </span>
              )}
              {indicators.length > 0 && (
                <span className={styles.indicators}>
                  {' with '}{indicators.join(', ')}
                </span>
              )}
            </h3>
          </div>
          <div className={styles.chartControls}>
            <button 
              className={styles.expandButton}
              onClick={() => setShowModal(true)}
              aria-label="Expand chart"
              title="Expand chart"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
              </svg>
            </button>
          </div>
        </div>
        
        <div className={styles.chartContainer}>
          {isLoading && (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <div>Loading chart...</div>
            </div>
          )}
          <div 
            ref={containerRef}
            className={styles.tradingViewContainer}
            style={{ 
              width: '100%', 
              height: '400px',
              opacity: isLoading ? 0 : 1,
              transition: 'opacity 0.3s ease'
            }}
          />
        </div>
      </div>

      {/* Chart Modal */}
      {showModal && (
        <ChartModal
          symbol={symbol}
          comparison_symbols={comparison_symbols}
          timeframe={timeframe}
          theme={theme}
          chart_type={chart_type}
          indicators={indicators}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
});

// Separate Modal Component for cleaner code
const ChartModal: React.FC<EmbeddedTradingViewChartProps & { onClose: () => void }> = ({
  symbol,
  comparison_symbols = [],
  timeframe = 'D',
  theme: themeProp,
  chart_type = 'single',
  indicators = [],
  onClose
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { theme: appTheme } = useTheme();
  const theme = themeProp ?? appTheme;

  useEffect(() => {
    if (!modalRef.current || !symbol) return;

    // Clear any existing content
    modalRef.current.innerHTML = '';

    // Create the widget container for modal - full size
    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container';
    widgetContainer.style.height = '100%';
    widgetContainer.style.width = '100%';
    widgetContainer.style.position = 'relative';

    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'tradingview-widget-container__widget';
    widgetDiv.style.height = '100%';
    widgetDiv.style.width = '100%';
    widgetDiv.style.position = 'absolute';
    widgetDiv.style.top = '0';
    widgetDiv.style.left = '0';

    // No copyright div in modal for maximum space utilization
    widgetContainer.appendChild(widgetDiv);

    // Create and configure the script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;

    // Format symbols
    const formatSymbol = (sym: string): string => {
      const upperSym = sym.toUpperCase();
      if (upperSym.includes(':')) return upperSym;
      
      const nyseArcaSymbols = ['SPY', 'QQQ', 'IWM', 'EFA', 'VTI', 'VOO', 'IVV', 'XLF', 'XLE', 'XLK'];
      if (nyseArcaSymbols.includes(upperSym)) return `AMEX:${upperSym}`;
      
      const nasdaqStocks = [
        'AAPL', 'MSFT', 'GOOGL', 'GOOG', 'AMZN', 'TSLA', 'META', 'NVDA', 'NFLX',
        'ADBE', 'CRM', 'INTC', 'AMD', 'PYPL', 'NFLX', 'CMCSA', 'PEP', 'COST',
        'AVGO', 'TXN', 'QCOM', 'INTU', 'AMGN', 'SBUX', 'GILD', 'MDLZ', 'ISRG'
      ];
      if (nasdaqStocks.includes(upperSym)) return `NASDAQ:${upperSym}`;
      
      const nyseStocks = [
        'JPM', 'JNJ', 'V', 'PG', 'UNH', 'HD', 'MA', 'DIS', 'BAC', 'XOM',
        'WMT', 'CVX', 'LLY', 'ABBV', 'PFE', 'KO', 'TMO', 'ACN', 'NKE', 'MRK',
        'DHR', 'VZ', 'ABT', 'ORCL', 'CRM', 'ADBE', 'WFC', 'BMY', 'PM', 'T'
      ];
      if (nyseStocks.includes(upperSym)) return `NYSE:${upperSym}`;
      
      return `NASDAQ:${upperSym}`;
    };

    // Map indicators
    const mapIndicators = (indicatorList: string[]): any[] => {
      const indicatorMap: { [key: string]: any } = {
        'RSI': { id: 'RSI@tv-basicstudies', inputs: { length: 14 } },
        'MACD': { id: 'MACD@tv-basicstudies', inputs: { fastLength: 12, slowLength: 26, signalLength: 9 } },
        'BB': { id: 'BB@tv-basicstudies', inputs: { length: 20, mult: 2 } },
        'MA': { id: 'MA@tv-basicstudies', inputs: { length: 50 } },
        'EMA': { id: 'EMA@tv-basicstudies', inputs: { length: 21 } },
        'Stoch': { id: 'Stoch@tv-basicstudies', inputs: { k: 14, d: 3, smooth: 3 } },
        'ATR': { id: 'ATR@tv-basicstudies', inputs: { length: 14 } },
        'VWAP': { id: 'VWAP@tv-basicstudies', inputs: {} },
        'OBV': { id: 'OBV@tv-basicstudies', inputs: {} },
        'ADX': { id: 'ADX@tv-basicstudies', inputs: { adxlen: 14, dilen: 14 } },
        'PSAR': { id: 'PSAR@tv-basicstudies', inputs: { start: 0.02, increment: 0.02, maximum: 0.2 } },
        'CCI': { id: 'CCI@tv-basicstudies', inputs: { length: 20 } },
        'WilliamsR': { id: 'WilliamsR@tv-basicstudies', inputs: { length: 14 } }
      };
      return indicatorList.map(indicator => indicatorMap[indicator]).filter(study => study !== undefined);
    };

    const formattedSymbol = formatSymbol(symbol);
    const formattedComparisonSymbols = comparison_symbols.map(formatSymbol);
    
    let chartStyle = '1';
    if (chart_type === 'comparison' || chart_type === 'overlay') {
      chartStyle = '3';
    }
    
    // For overlay and comparison charts, use proper object format
    const compareSymbolsConfig = formattedComparisonSymbols.map(sym => ({
      symbol: sym,
      position: "SameScale" // This ensures overlay behavior
    }));
    
    const studies = mapIndicators(indicators);
    
    const config = {
      autosize: true,
      symbol: formattedSymbol,
      interval: timeframe,
      timezone: 'Etc/UTC',
      theme: theme,
      style: chartStyle,
      locale: 'en',
      backgroundColor: '#131722',
      gridColor: 'rgba(240, 243, 250, 0.06)',
      allow_symbol_change: true,
      calendar: false,
      details: true, // Show details for better UX
      hide_side_toolbar: false, // Keep toolbar for full functionality
      hide_top_toolbar: false, // Keep top toolbar for timeframe selection
      hide_legend: false, // Always show legend for better visibility
      hide_volume: chart_type === 'overlay', // Only hide volume for overlay charts
      hotlist: false,
      save_image: true,
      withdateranges: true,
      range: "12M", // Longer range for modal
      studies: studies,
      watchlist: [],
      container_id: `tradingview_modal_${symbol}_${Date.now()}`,
      width: "100%",
      height: "100%",
      // Add comparison symbols if present - use proper format for TradingView embedded widget
      ...(compareSymbolsConfig.length > 0 && {
        compareSymbols: compareSymbolsConfig
      })
    };

    script.innerHTML = JSON.stringify(config);
    widgetDiv.appendChild(script);
    modalRef.current.appendChild(widgetContainer);

    // Force a resize event after the widget loads to ensure proper sizing
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 1000);

    // Capture ref to avoid stale closure
    const currentModalRef = modalRef.current;
    return () => {
      if (currentModalRef) {
        currentModalRef.innerHTML = '';
      }
    };
  }, [symbol, comparison_symbols, timeframe, theme, chart_type, indicators]);

  return (
    <div className={styles.chartModal} onClick={onClose}>
      <div className={styles.chartModalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.chartModalHeader}>
          <h3 className={styles.modalTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"></polyline>
            </svg>
            {symbol.toUpperCase()}
            {comparison_symbols.length > 0 && (
              <span>
                {chart_type === 'overlay' ? ' + ' : ' vs '}
                {comparison_symbols.map(s => s.toUpperCase()).join(chart_type === 'overlay' ? ' + ' : ', ')}
              </span>
            )}
            {indicators.length > 0 && (
              <span className={styles.indicators}>
                {' with '}{indicators.join(', ')}
              </span>
            )}
          </h3>
          <button className={styles.modalCloseButton} onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className={styles.chartModalBody}>
          <div 
            ref={modalRef}
            className={styles.modalTradingViewContainer}
          />
        </div>
      </div>
    </div>
  );
};

EmbeddedTradingViewChart.displayName = 'EmbeddedTradingViewChart';